import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { formatMinutes } from "@/components/entry/RuntimeChip.tsx";
import { formatHoursPerDay } from "@/lib/countdown/format.ts";
import { useCountdown } from "@/lib/countdown/useCountdown.ts";
import { useStuck } from "@/lib/useStuck.ts";
import { CountdownLeg } from "./CountdownLeg.tsx";
import styles from "./countdown.module.css";
import { EncoreToggle } from "./EncoreToggle.tsx";
import { PaceBudget } from "./PaceBudget.tsx";

/**
 * The sticky countdown: one leg to Doomsday, or two once Encore mode splits
 * the road at the Endgame Encore.
 *
 * It earns its space twice. At rest it's a full panel — rings, pace meters,
 * and the callout for any leg that doesn't fit. Once you scroll past it, the
 * sentinel above flips `data-stuck` and CSS collapses it to a strip that keeps
 * only the two numbers you're actually tracking while you read the road: days
 * left and hours per day.
 */
export function CountdownBar() {
  const { encore, milestone, legs, road, encoreLeadDays, worstStatus } = useCountdown();
  const { stuck, sentinelRef } = useStuck();
  const barRef = useRef<HTMLElement | null>(null);
  const slotRef = useRef<HTMLDivElement | null>(null);
  useHeldSlot(barRef, slotRef, stuck);
  usePublishedHeight(barRef, stuck);

  const leg1 = encore ? legs[0] : null;

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
      {/*
       * The slot holds the expanded height open. See useHeldSlot — without
       * it, collapsing the pinned bar deletes space from above where you are
       * reading and the whole page jumps upward under you.
       */}
      <div ref={slotRef} className={styles.slot} data-stuck={stuck}>
        <section
          ref={barRef}
          className={styles.bar}
          data-stuck={stuck}
          data-status={worstStatus}
          aria-label="Countdown"
        >
          <div className={styles.inner}>
            <div className={styles.top}>
              <EncoreToggle milestone={milestone} leadDays={encoreLeadDays} />
              <PaceBudget />
            </div>

            {/* The beat: the Encore deadline is not a footnote in December, it's
              weeks away, and it owns a specific, countable slice of the road. */}
            {leg1 && (
              <p className={styles.beat}>
                <strong>
                  Leg 1 is due in {leg1.daysLeft} days, not {road.daysLeft}.
                </strong>{" "}
                {leg1.remainingCount} stops and {formatMinutes(leg1.remainingMinutes)} have to
                happen before the Encore, at {formatHoursPerDay(leg1.requiredHoursPerDay)} h/day on
                their own. Doomsday is the <em>second</em> deadline.
              </p>
            )}

            <div className={styles.legs} data-count={legs.length}>
              {legs.map((leg) => (
                <CountdownLeg key={leg.id} leg={leg} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

/**
 * Keeps the countdown's footprint constant while it collapses.
 *
 * The bar is sticky, and a sticky element keeps its original slot in normal
 * flow even while it is pinned to the top of the viewport. Once you have
 * scrolled past, that slot sits ABOVE where you are reading — so shrinking the
 * bar from its full panel to its compact strip deletes ~157px from above your
 * scroll position, and every pixel below it lurches upward. The symptom is
 * scrolling down from the top and being thrown back up, which is exactly when
 * the collapse fires.
 *
 * The fix is to stop the collapse changing layout at all: measure the bar at
 * its full height while it is unpinned, pin that onto the wrapper, and let the
 * bar shrink inside a slot that never moves.
 */
function useHeldSlot(
  barRef: RefObject<HTMLElement | null>,
  slotRef: RefObject<HTMLDivElement | null>,
  stuck: boolean,
): void {
  const expanded = useRef<number | null>(null);

  // Layout effect, not effect, and the distinction is the whole fix. Flipping
  // data-stuck takes the bar out of flow, which collapses the slot to nothing
  // in the same paint. Restoring the held height afterwards means one frame
  // where the page is ~290px shorter — a visible flinch, which is the bug in
  // miniature. Running before paint means the reader never sees that frame.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const slot = slotRef.current;
    if (!bar || !slot) return;

    // Only measurable while unpinned; that is the only time the bar is at its
    // full size. Re-measured on resize, because the strip is one leg or two
    // and stacks differently on a phone.
    if (stuck) {
      if (expanded.current !== null) slot.style.height = `${expanded.current}px`;
      return;
    }

    slot.style.height = "";
    const measure = () => {
      const height = Math.round(bar.getBoundingClientRect().height);
      if (height > 0) expanded.current = height;
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [barRef, slotRef, stuck]);
}

/**
 * Publish the pinned bar's real height as `--road-countdown-h`, which is what
 * the control-panel sidebar and section anchors offset themselves by.
 *
 * It can't be a constant in CSS: the strip is one leg or two, side by side on
 * a desktop and stacked on a phone. Only measured while pinned — that's the
 * only state in which the bar covers anything — and the last measurement
 * stands afterwards, since nothing consults the variable while the bar is
 * sitting in normal flow.
 */
function usePublishedHeight(barRef: RefObject<HTMLElement | null>, stuck: boolean): void {
  useEffect(() => {
    const bar = barRef.current;
    if (!bar || !stuck || typeof ResizeObserver === "undefined") return;
    const publish = () => {
      const height = Math.round(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty("--road-countdown-h", `${height}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [barRef, stuck]);
}
